import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import CurrencyInputAdornment from 'components/currency/CurrencyInputAdornment';
import DirhamSymbol from 'components/currency/DirhamSymbol';
import { useToast } from 'contexts/ToastContext';
import { refundTransaction } from 'services/payment.service';
import { brand } from 'utils/designTokens';

const REASON_OPTIONS = [
  { value: 'requested_by_customer', label: 'Requested by customer' },
  { value: 'duplicate', label: 'Duplicate charge' },
  { value: 'fraudulent', label: 'Fraudulent activity' }
];

export default function RefundTransactionDialog({ open, item, onClose, onSuccess }) {
  const { showToast, showApiError } = useToast();

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [serverError, setServerError] = useState('');

  const targetId =
    item?.transaction?.id ||
    item?.transaction?.transaction_id ||
    item?.order?.id ||
    item?.order_id ||
    item?.id;

  const orderNumber =
    item?.order_no ||
    item?.order?.order_no ||
    (item?.order_id ? `#${item.order_id}` : item?.order?.id ? `#${item.order.id}` : `#${targetId}`);

  const customerName = item?.customer?.name || item?.order?.customer?.name || '—';

  const txId = item?.transaction?.id || item?.transaction?.transaction_id || (item?.gateway ? item?.id : null);
  const stripeRef =
    item?.transaction?.stripe_charge_id ||
    item?.stripe_charge_id ||
    item?.transaction?.stripe_payment_intent_id ||
    item?.stripe_payment_intent_id;

  const remaining = Number(
    item?.financials?.remaining_refundable_amount ??
      item?.remaining_refundable_amount ??
      item?.paid_amount ??
      0
  );

  useEffect(() => {
    if (open && item) {
      setAmount(remaining > 0 ? String(remaining) : '');
      setReason('requested_by_customer');
      setNote('');
      setError('');
      setServerError('');
      setSubmitting(false);
    }
  }, [open, item, remaining]);

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);
    setError('');
    setServerError('');

    if (val !== '' && !isNaN(Number(val))) {
      const num = Number(val);
      if (num <= 0) {
        setError('Refund amount must be greater than zero.');
      } else if (remaining > 0 && num > remaining) {
        setError(`Amount cannot exceed remaining refundable balance of ${remaining.toFixed(2)} AED.`);
      }
    }
  };

  const handleProcessRefund = async () => {
    if (submitting || !targetId) return;

    let parsedAmount;
    if (amount !== '' && amount !== null && amount !== undefined) {
      parsedAmount = Number(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        setError('Please enter a valid positive refund amount.');
        return;
      }
      if (remaining > 0 && parsedAmount > remaining) {
        setError(`Amount cannot exceed remaining refundable balance of ${remaining.toFixed(2)} AED.`);
        return;
      }
    }

    setSubmitting(true);
    setServerError('');
    try {
      const idempotencyKey = `admin_tx_refund_${targetId}_${Date.now()}`;
      const res = await refundTransaction(targetId, {
        amount: parsedAmount,
        reason,
        note: note.trim(),
        idempotency_key: idempotencyKey
      });

      showToast(res?.message || 'Refund processed successfully via Stripe.', {
        severity: 'success'
      });

      if (onSuccess) {
        onSuccess(res?.data);
      }
      onClose();
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        'Failed to process refund.';
      setServerError(errorMsg);
      showApiError(err, 'Failed to process refund.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CurrencyExchangeIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Issue Stripe Refund
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <Alert severity="info" sx={{ bgcolor: brand.canvasAlt, border: `1px solid ${brand.border}` }}>
            This will directly refund the customer&apos;s card via Stripe and record an audit entry on the
            order ledger.
          </Alert>

          {serverError && (
            <Alert severity="error" onClose={() => setServerError('')} sx={{ wordBreak: 'break-word' }}>
              {serverError}
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'background.default',
              border: `1px solid ${brand.border}`
            }}
          >
            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Order Number:
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {orderNumber}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Customer:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {customerName}
              </Typography>
            </Stack>

            {txId && (
              <Stack direction="row" justifyContent="space-between" mb={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Transaction ID:
                </Typography>
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}
                >
                  {txId}
                </Typography>
              </Stack>
            )}

            {stripeRef && (
              <Stack direction="row" justifyContent="space-between" mb={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Stripe Reference:
                </Typography>
                <Typography
                  variant="caption"
                  fontFamily="monospace"
                  sx={{ bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1 }}
                >
                  {stripeRef}
                </Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Available Refundable Balance:
              </Typography>
              <CurrencyAmount value={remaining} fontWeight={700} color="primary.main" />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Refund Amount
              </Typography>
              <DirhamSymbol size={13} />
            </Stack>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              disabled={submitting}
              error={Boolean(error)}
              helperText={
                error ||
                `Maximum refundable amount: ${remaining.toFixed(2)} AED. Leave default for full remaining balance.`
              }
              slotProps={{
                input: {
                  startAdornment: <CurrencyInputAdornment />,
                  inputProps: { min: 0.01, max: remaining > 0 ? remaining : undefined, step: 0.01 }
                }
              }}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel id="refund-reason-label">Stripe Reason</InputLabel>
            <Select
              labelId="refund-reason-label"
              value={reason}
              label="Stripe Reason"
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            >
              {REASON_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Admin Note (Optional)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="e.g. Compensation for late delivery or missing item."
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 150))}
              disabled={submitting}
              helperText={`${note.length}/150 characters`}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleProcessRefund}
          variant="contained"
          disabled={submitting || Boolean(error) || !targetId || remaining <= 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CurrencyExchangeIcon />}
          sx={{
            bgcolor: brand.primary,
            '&:hover': { bgcolor: brand.primaryDark }
          }}
        >
          {submitting ? 'Refunding...' : 'Confirm Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RefundTransactionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
